import { type MetaFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [
	{ title: 'Terms of Service | Oui Technologies' },
	{
		name: 'description',
		content:
			'Terms of Service and Software License Agreement for Oui Technologies platform.',
	},
]

export default function TermsOfServiceRoute() {
	return (
		<div className="bg-white py-16 px-6">
			<div className="max-w-3xl mx-auto">
				<h1 className="text-4xl font-bold mb-2">
					Terms of Service &amp; Software License Agreement
				</h1>
				<p className="text-sm text-gray-500 mb-10">Last Updated: June 12, 2026</p>

				<p className="mb-8 text-gray-700">
					These Terms of Service ("Terms") govern access to and use of the Oui
					Technologies platform (the "Platform"), provided by Oui Technologies, a
					business registered in the State of Wisconsin, USA ("Oui," "we," "us").
					By creating an account or using the Platform, you ("Customer," "you")
					agree to these Terms.
				</p>

				<Section number="1" title="License Grant">
					<p>
						Subject to your compliance with these Terms and payment of applicable
						fees, Oui grants you a limited, non-exclusive, non-transferable,
						revocable license to access and use the Platform for your internal
						business operations during the term of your subscription.
					</p>
					<p className="mt-3">You may not:</p>
					<ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
						<li>
							Reverse-engineer, decompile, or attempt to extract source code from
							the Platform
						</li>
						<li>
							Sublicense, resell, or provide third-party access to the Platform
							without written consent
						</li>
						<li>Use the Platform to build a competing product</li>
					</ul>
				</Section>

				<Section number="2" title="Account & Acceptable Use">
					<p>You are responsible for:</p>
					<ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
						<li>The accuracy of information entered into the Platform</li>
						<li>
							Maintaining the confidentiality of your login credentials
						</li>
						<li>
							All activity occurring under your account, including by employees
							you authorize
						</li>
						<li>
							Obtaining any necessary consents from employees before entering
							their personal information for scheduling purposes (see Privacy
							Policy, Section 2.2)
						</li>
					</ul>
				</Section>

				<Section number="3" title="Data Ownership">
					<ul className="space-y-3 text-gray-700">
						<li>
							<span className="font-semibold">Your Data.</span> You retain
							ownership of all invoice data, inventory data, recipe data, and
							employee data you input into the Platform ("Customer Data").
						</li>
						<li>
							<span className="font-semibold">Derived Data.</span> Oui retains
							ownership of the Platform software, algorithms, and any aggregated
							or de-identified data derived from Customer Data that cannot be
							used to identify you or your business.
						</li>
						<li>
							<span className="font-semibold">License Back.</span> You grant Oui
							a limited license to use Customer Data solely to provide the
							Platform's services to you (invoice processing, QuickBooks sync,
							inventory/recipe routing).
						</li>
					</ul>
				</Section>

				<Section number="4" title="Third-Party Integrations">
					<p>
						The Platform integrates with QuickBooks (Intuit) and is hosted on
						Fly.io infrastructure. Use of QuickBooks integration is subject to
						Intuit's own terms and privacy practices, which are outside Oui's
						control. Oui is not responsible for the availability, accuracy, or
						practices of third-party services.
					</p>
				</Section>

				<Section number="5" title="Invoice Processing Disclaimer">
					<p>
						The Platform uses automated image recognition (OCR) to extract data
						from invoice images.{' '}
						<span className="font-semibold">
							Oui does not guarantee 100% accuracy of extracted data.
						</span>{' '}
						You are responsible for reviewing invoice data — particularly before
						it routes to QuickBooks, inventory, or recipe costing — and Oui is
						not liable for losses resulting from inaccurate automated extraction
						that was not corrected by you prior to use.
					</p>
				</Section>

				<Section number="6" title="Fees & Payment">
					<div className="space-y-4 text-gray-700">
						<div>
							<p className="font-semibold">
								6.1 Platform Subscription (Restaurant Customers).
							</p>
							<p>
								Customers accessing the full Platform shall pay a monthly
								subscription fee of $250.00/month.
							</p>
						</div>
						<div>
							<p className="font-semibold">
								6.2 QuickBooks Integration Service (Accounting Firm Customers).
							</p>
							<p>
								Customers accessing the QuickBooks invoice integration service
								shall pay $0.25 per invoice processed, billed monthly based on
								invoice volume for the preceding billing period.
							</p>
						</div>
						<div>
							<p className="font-semibold">6.3 Payment Terms.</p>
							<p>
								All fees are due within thirty (30) days of the invoice date
								("Due Date").
							</p>
						</div>
						<div>
							<p className="font-semibold">6.4 Late Payment.</p>
							<p>
								Any payment not received within thirty (30) days of the Due Date
								shall accrue a late fee of $5.00 per day, up to a maximum of
								$150.00. If payment, including accrued late fees, is not received
								in full, Oui may suspend or terminate access to the Platform per
								Section 7.
							</p>
						</div>
						<div>
							<p className="font-semibold">6.5 Fee Changes.</p>
							<p>
								Oui may modify its fees upon thirty (30) days' written notice.
								Continued use of the Platform after the effective date of a fee
								change constitutes acceptance of the new fees.
							</p>
						</div>
					</div>
				</Section>

				<Section number="7" title="Term & Termination">
					<ul className="space-y-2 list-disc list-inside text-gray-700">
						<li>
							Either party may terminate for convenience with 30 days' written
							notice.
						</li>
						<li>
							Oui may suspend or terminate access immediately for breach of these
							Terms, non-payment, or misuse of the Platform.
						</li>
						<li>
							Upon termination, you may request export of Customer Data within 30
							days; after that period, Oui may delete Customer Data per the
							Privacy Policy.
						</li>
					</ul>
				</Section>

				<Section number="8" title="Warranties & Disclaimers">
					<p className="uppercase text-sm text-gray-700 leading-relaxed">
						The Platform is provided "as is" and "as available." Oui disclaims
						all warranties, express or implied, including merchantability, fitness
						for a particular purpose, and non-infringement, except as expressly
						stated herein.
					</p>
				</Section>

				<Section number="9" title="Limitation of Liability">
					<p className="uppercase text-sm text-gray-700 leading-relaxed">
						To the maximum extent permitted by law, Oui's total liability arising
						out of or related to these Terms shall not exceed three thousand
						dollars ($3,000). Oui shall not be liable for indirect, incidental,
						consequential, or punitive damages, including lost profits or lost
						data, even if advised of the possibility.
					</p>
				</Section>

				<Section number="10" title="Indemnification">
					<p>
						You agree to indemnify and hold Oui harmless from claims arising
						from: (a) your breach of these Terms; (b) your failure to obtain
						necessary consents for employee data entered into the Platform; (c)
						your misuse of Platform-derived inventory, recipe, or invoice data.
					</p>
				</Section>

				<Section number="11" title="Confidentiality">
					<p>
						Each party agrees to keep the other's confidential business
						information (including Customer Data, on Oui's side, and Platform
						technology, on Oui's side) confidential and not disclose it except as
						required by these Terms or law.
					</p>
				</Section>

				<Section number="12" title="Modifications to Terms">
					<p>
						Oui may update these Terms from time to time. Continued use of the
						Platform after changes take effect constitutes acceptance. Material
						changes will be communicated via email or in-app notice.
					</p>
				</Section>

				<Section number="13" title="Governing Law & Dispute Resolution">
					<p>
						These Terms are governed by the laws of the State of Wisconsin, USA.
						Any disputes shall be resolved in the state or federal courts located
						in Wisconsin.
					</p>
				</Section>

				<Section number="14" title="Entire Agreement">
					<p>
						These Terms, together with the Privacy Policy, constitute the entire
						agreement between you and Oui regarding the Platform and supersede
						any prior agreements.
					</p>
				</Section>

				<Section number="15" title="Contact">
					<address className="not-italic text-gray-700 space-y-1">
						<p className="font-semibold">Oui Technologies</p>
						<p>1818 Thackeray Rd.</p>
						<p>Madison, WI 53704</p>
						<p>
							<a
								href="mailto:seanmfogarty@gmail.com"
								className="text-primary underline"
							>
								seanmfogarty@gmail.com
							</a>
						</p>
					</address>
				</Section>
			</div>
		</div>
	)
}

function Section({
	number,
	title,
	children,
}: {
	number: string
	title: string
	children: React.ReactNode
}) {
	return (
		<section className="mb-10">
			<h2 className="text-xl font-bold mb-3 text-gray-900">
				{number}. {title}
			</h2>
			<div className="text-gray-700 leading-relaxed">{children}</div>
		</section>
	)
}
